// Enums moved here to break circular dependency
export enum AUDIENCE_CREATION_METHOD {
  RULES = 'rules',
  CSV = 'csv',
  IMAGES = 'images',
  VOICE = 'voice_recordings',
  TIME_BASED = 'scheduled',
  SCHEDULED_IMPORT = 'scheduled_import',
  // #3365 FE-routing key only — the webhook creation form sends creation_method
  // 'scheduled_import' with rules[0].source='webhook'; the backend never returns 'webhook'.
  WEBHOOK = 'webhook',
  CHROME_EXTENSION = 'chrome_extension',
  HUBSPOT = 'hubspot',
  SALESFORCE = 'salesforce',
  WEBSITE_VISITS = 'website_visits',
  GTM_SIGNAL = 'gtm_signal',
  SALESFORCE_CTI = 'salesforce_cti',
  SINGLE_CALL = 'single_call',
  LINKEDIN_POST_COMMENTERS = 'linkedin_post_commenters',
  CONFERENCE_SCOUT = 'conference_scout',
  PRE_MEETING_BRIEF = 'pre_meeting_brief',
  AUDIENCE_BUILDER = 'audience_builder',
  FIBER_TAM = 'fiber_tam',
  POWER_DIALER = 'power_dialer',
  // Persistent ready-to-call audience maintained by an Add to Dialer workflow
  // node (epic #3561). Unlike POWER_DIALER (ephemeral session copies), these
  // are workspace-visible and eligible in the Power Dialer picker.
  DIALER_QUEUE = 'dialer_queue',
  // A rep's nurture loop audience (mb-frontend#4458); created by the server, never by a form.
  LOOP = 'loop',
  AI_SEARCH = 'ai_search',
  SIXSENSE = 'sixsense_accounts',
}

/**
 * Audiences whose workflows support batch_size, batch_frequency, and reset_processing
 * in the Run workflow UI and API (aligned with mb-server list-based execution).
 */
export const WORKFLOW_BATCH_CREATION_METHODS: readonly AUDIENCE_CREATION_METHOD[] = [
  AUDIENCE_CREATION_METHOD.CSV,
  AUDIENCE_CREATION_METHOD.HUBSPOT,
  AUDIENCE_CREATION_METHOD.SALESFORCE,
  AUDIENCE_CREATION_METHOD.GTM_SIGNAL,
  AUDIENCE_CREATION_METHOD.LINKEDIN_POST_COMMENTERS,
  AUDIENCE_CREATION_METHOD.CHROME_EXTENSION,
  AUDIENCE_CREATION_METHOD.AI_SEARCH,
  AUDIENCE_CREATION_METHOD.AUDIENCE_BUILDER,
  AUDIENCE_CREATION_METHOD.FIBER_TAM,
] as const;

export function audienceSupportsWorkflowBatchUI(creationMethod: string | undefined): boolean {
  if (creationMethod === undefined) return false;
  return (WORKFLOW_BATCH_CREATION_METHODS as readonly string[]).includes(creationMethod);
}

/**
 * Mirrors mb-server's LIST_BASED_CREATION_METHODS: the types whose start_time is a list
 * start, which mb-server#5868 honors only for the methods its CsvAudienceBatchJob polls.
 */
const LIST_START_CREATION_METHODS: readonly AUDIENCE_CREATION_METHOD[] = [
  AUDIENCE_CREATION_METHOD.CSV,
  AUDIENCE_CREATION_METHOD.HUBSPOT,
  AUDIENCE_CREATION_METHOD.IMAGES,
  AUDIENCE_CREATION_METHOD.WEBSITE_VISITS,
  AUDIENCE_CREATION_METHOD.LINKEDIN_POST_COMMENTERS,
  AUDIENCE_CREATION_METHOD.GTM_SIGNAL,
  AUDIENCE_CREATION_METHOD.CHROME_EXTENSION,
  AUDIENCE_CREATION_METHOD.SALESFORCE_CTI,
  AUDIENCE_CREATION_METHOD.AI_SEARCH,
  AUDIENCE_CREATION_METHOD.SALESFORCE,
  AUDIENCE_CREATION_METHOD.AUDIENCE_BUILDER,
  AUDIENCE_CREATION_METHOD.FIBER_TAM,
  AUDIENCE_CREATION_METHOD.POWER_DIALER,
  AUDIENCE_CREATION_METHOD.SCHEDULED_IMPORT,
  AUDIENCE_CREATION_METHOD.SIXSENSE,
] as const;

/**
 * Mirrors mb-server's CSV_AUDIENCE_BATCH_JOB_CREATION_METHODS: the list types whose stored
 * start time is the schedule. Any other list type would need a queued message that could
 * not be cancelled on reschedule, so the server rejects a later start for it.
 */
const SCHEDULED_START_CREATION_METHODS: readonly AUDIENCE_CREATION_METHOD[] = [
  AUDIENCE_CREATION_METHOD.CSV,
  AUDIENCE_CREATION_METHOD.HUBSPOT,
  AUDIENCE_CREATION_METHOD.SALESFORCE,
  AUDIENCE_CREATION_METHOD.AUDIENCE_BUILDER,
  AUDIENCE_CREATION_METHOD.CHROME_EXTENSION,
] as const;

/** False for a list audience that can only run now; non-list audiences keep their schedule option. */
export function audienceSupportsScheduledStart(creationMethod: string | undefined): boolean {
  if (creationMethod === undefined) return true;
  if (!(LIST_START_CREATION_METHODS as readonly string[]).includes(creationMethod)) return true;
  return (SCHEDULED_START_CREATION_METHODS as readonly string[]).includes(creationMethod);
}

export enum AUDIENCE_CSV_IMPORT_TYPE {
  PERSON = 'person',
  COMPANY = 'company',
}

export type AudienceOperator = 'and' | 'or';
export type HubSpotOperator =
  | 'EQ' | 'NEQ'
  | 'CONTAINS_TOKEN' | 'NOT_CONTAINS_TOKEN'
  | 'IN' | 'NOT_IN'
  | 'GT' | 'GTE' | 'LT' | 'LTE' | 'BETWEEN'
  | 'EQ_ANY'
  | 'HAS_PROPERTY' | 'NOT_HAS_PROPERTY';

export interface HubSpotListItem {
  id: number | string;
  name: string;
  size?: number;
  processingType?: string;
  [key: string]: unknown;
}

export interface HubSpotAuthErrorData {
  code?: string;
  message?: string;
  requiresReconnect?: boolean;
}

export interface HubSpotApiError {
  response?: {
    data?: HubSpotAuthErrorData;
  };
}

export interface HubSpotPreviewContact {
  id?: string | number;
  hs_record_id?: string;
  hs_object_id?: string;
  first_name?: string;
  last_name?: string;
  firstname?: string;
  lastname?: string;
  name?: string;
  email?: string;
  company?: string;
  company_name?: string;
  [key: string]: unknown;
}

export interface HubSpotPreviewResults {
  contacts?: HubSpotPreviewContact[];
  total?: number;
  field_mappings?: Record<string, string>;
  [key: string]: unknown;
}
export interface IAudienceForm {
  id?: number;
  name: string;
  permissions: any;
  chat_token: string;
  campaign_name?: string | null;
  conditionGroups: {
    groupOperator?: AudienceOperator;
    logicalOperator?: AudienceOperator;
    conditions: {
      id?: string;
      fact?: string;
      operator?: string;
      value?: string;
      conditionLogicalOperator?: AudienceOperator;
    }[];
  }[];
  type: string;
  list_id?: number;
  hubspot_list_id?: number;
  hubspot_field?: string;
  hubspot_value?: string;
  hubspot_operator?: HubSpotOperator;
  hubspot_object_type?: string;
  hubspot_filter_groups?: Array<Record<string, unknown>>;
  cached_contacts?: any[];
  cached_mappings?: any;
  total_count?: number;
  creation_method: AUDIENCE_CREATION_METHOD;
  start_time?: any;
  voice_recordings?: string;
  webhook_id?: number;
  schedule_config?: {
    frequency: string;
    days: number[];
    dayOfWeek: number[];
    times: string[];
    minutes: string[];
    months: number[];
    /** Present when frequency = 'INTERVAL_DAYS'. Number of days between runs. */
    interval_days?: number;
  };
  timezone?: string;
  batch_size?: number;
  batch_frequency?: string;
  reset_processing?: boolean;
  closed_lost_rule_id?: number | null;
  days_since_closed_lost?: number | null;
  dispatch_daily_cap?: number | null;
}

export interface IFact {
  name: string;
  description: string;
  data_type: 'number' | 'string' | 'boolean';
  category: string;
  example_value: string;
  operators: string[];
  icon: string;
  placeholder: string;
}

export interface ICreateAudienceBody {
  name: string;
  permissions: string;
  rules?: any;
  chat_token?: string;
  list_id?: number;
  type?: string;
  creation_method?: AUDIENCE_CREATION_METHOD;
  start_time?: string;
  voice_recordings?: string;
  webhook_id?: number;
  schedule_config?: {
    frequency: string;
    days: number[];
    dayOfWeek: number[];
    times: string[];
    minutes: string[];
    months: number[];
    interval_days?: number;
  };
  timezone?: string;
  batch_size?: number;
  batch_frequency?: string;
  reset_processing?: boolean;
  hubspot_field?: string;
  hubspot_value?: string;
  hubspot_operator?: HubSpotOperator;
  hubspot_list_id?: number;
  hubspot_object_type?: string;
  cached_contacts?: any[];
  cached_mappings?: any;
  total_count?: number;
  campaign_name?: string | null;
  tags?: string[];
  shared_with_team?: boolean;
  description?: string | null;
  closed_lost_rule_id?: number | null;
  days_since_closed_lost?: number | null;
  dispatch_daily_cap?: number | null;
}

export type IUpdateAudienceBody = Partial<ICreateAudienceBody> & {
  user_id?: string;
};

export type AudienceTriggerType = 'manual' | 'webhook' | null;

export interface TriggerFilter {
  mode: 'field_match' | 'llm';
  path?: string;
  op?: 'eq' | 'ne' | 'contains';
  value?: string;
  prompt?: string;
}

export interface IAudienceTriggerWebhook {
  endpoint_url: string;
  has_secret: boolean;
  status: 'waiting' | 'active' | 'error';
  last_event_at?: string | null;
}

/**
 * Set by the server when an import gave the customer less than the source matched
 * (mb-server#6298). `matched` is null when the source pages without a total, as HubSpot does.
 * Absent/null means there is nothing to report, which is the common case.
 */
export interface IAudienceImportNotice {
  kind: 'cap_reached';
  source: 'salesforce' | 'hubspot';
  imported: number;
  matched: number | null;
  limit: number;
  at: string;
}

export interface IAudienceItem {
  import_notice?: IAudienceImportNotice | null;
  id: number;
  name: string;
  user_id: string;
  vendorid: number;
  permissions: string;
  created_at: string;
  updated_at: string;
  chat_token: string;
  createdBy?: {
    firstName: string;
    lastName: string;
  };
  rules?: {
    conditions: any;
  } | Array<{
    conditions: {
      all?: Array<{
        fact: string;
        operator: string;
        value: any;
      }>;
      any?: Array<{
        fact: string;
        operator: string;
        value: any;
      }>;
    };
  }> | null;
  list_id?: number;
  type: string;
  creation_method: AUDIENCE_CREATION_METHOD;
  is_system?: boolean;
  is_editable?: boolean;
  is_deletable?: boolean;
  start_time?: string;
  voice_recordings?: string;
  webhook_id?: number;
  schedule_config?: {
    frequency: string;
    days: number[];
    dayOfWeek: number[];
    times: string[];
    minutes: string[];
    months: number[];
    interval_days?: number;
  };
  timezone?: string;
  batch_size?: number;
  batch_frequency?: string;
  next_batch_process_time?: string | null;
  last_processed_offset?: number | null;
  title?: string;
  description?: string;
  hubspot_field?: string;
  hubspot_value?: string;
  hubspot_operator?: HubSpotOperator;
  hubspot_list_id?: number;
  hubspot_object_type?: string;
  cached_contacts?: any[];
  cached_mappings?: any;
  total_count?: number;
  website_visit_filters?: {
    type?: 'person' | 'company';
    signalType?: 'website' | 'ad-viewers' | 'all';
    timeFilter?: string | null;
    filters?: {
      excludeCustomers?: boolean;
      industries?: string[];
      companySizes?: string[];
      countries?: string[];
      campaigns?: string[];
      pageUrls?: string[];
      deviceTypes?: string[];
      namedAccountsOnly?: boolean;
      chatToken?: string;
      search?: string;
    };
    maxRecords?: number;
  };
  // Optional campaign name for the split Campaigns UI.
  // Falls back to `name` when null/undefined.
  campaign_name?: string | null;
  // Campaign wizard "Save & re-use" step
  tags?: string[];
  shared_with_team?: boolean;
  // Workflow attachment status for "Run Audience" feature
  workflow_count?: number;
  inbox_count?: number;
  first_workflow_name?: string | null;
  /** A nurture loop audience (mb-frontend#4458): it runs itself, so its state replaces the campaign status. */
  loop?: { state: 'off' | 'on' | 'paused'; sends_enabled: boolean } | null;
  // Publish status - null means not published/active
  published_at?: string | null;
  // Derived workflow status based on play_executions. 'scheduled' = a real run is set to start
  // at a future time (audiences.pending_start_at) and nothing is running yet (mb-server#5868).
  derived_status?: 'new' | 'ready' | 'scheduled' | 'in_progress' | 'completed' | 'partial' | 'replied' | 'error' | 'stalled';
  /**
   * A rep cleared this campaign's "Needs attention" mark when it had this many failed steps
   * (`execution_stats.error`). The mark comes back once the count passes it. Null when not cleared.
   */
  attention_cleared_failed_steps?: number | null;
  attention_cleared_at?: string | null;
  execution_stats?: {
    total: number;
    completed: number;
    in_progress: number;
    replied: number;
    error: number;
    pending_next: number;
    total_people: number;
    processed: number;
    // Legacy fields for backwards compat — present on older execution records
    executed_nodes?: number;
    play_count?: number;
    // Fine-grained real-time state: active=running now, scheduled=next batch pending, in_delay=waiting between steps
    sub_status?: 'active' | 'scheduled' | 'in_delay' | null;
    // Count of executions on a confirmed-dead (stalled) chain
    stalled?: number;
    // Human-readable reason shown beneath a stalled status (present only when derived_status === 'stalled')
    stall_reason?: string | null;
    /**
     * When the newest failed step failed, as an opaque token. Sent back unchanged when the rep
     * clears Needs attention: any failure after it lifts the clear (mb-server#6822).
     */
    last_failure_at?: string | null;
  };
  // Live people/company count from list (null for non-list audiences)
  people_count?: number | null;
  processed_count?: number | null;
  // Campaigns Gallery card meta ("N steps · M prospects · ran Xd ago")
  step_count?: number;
  last_run_at?: string | null;
  // Aggregated email campaign metrics
  email_metrics?: {
    // Every total below is the sum of its own two halves; the halves are published beside
    // it (mb-server EmailMetrics) so a merged number is never all a reader gets. Email
    // counts sends, LinkedIn counts people — one lead row per campaign is not one message.
    emails_sent: number;
    opens: number;
    open_rate: number;
    replies: number;
    reply_rate: number;
    email_sent?: number;
    linkedin_sent?: number;
    email_opens?: number;
    linkedin_opens?: number;
    email_replies?: number;
    linkedin_replies?: number;
  };
  // Webhook trigger columns (added by 20260512 migration)
  trigger_type?: AudienceTriggerType;
  trigger_webhook_id?: number | null;
  trigger_filter?: TriggerFilter | null;
  trigger_webhook?: IAudienceTriggerWebhook | null;
  // Closed-lost re-engagement dispatch config (added by 20260218 + 20260520 migrations)
  closed_lost_rule_id?: number | null;
  days_since_closed_lost?: number | null;
  dispatch_daily_cap?: number | null;
  // Timestamp of the last successful scheduled_import run (added by 20260601 migration).
  // Used to compute "Last run" and "Next run" in the audience detail view.
  last_imported_at?: string | null;
}

export interface IAudienceCsvDataUploadedPagination {
  limit: number;
  page: number;
  total: number;
  totalPages: number;
}

export interface IAudiencePersonCsvDataUploadedItem {
  company_name: string;
  created_at: string;
  email: string;
  first_name: string;
  id: number;
  job_title: string;
  last_name: string;
  list_id: number;
  phone: string;
  /** Latest AMD-confirmed human Power Dialer connection for this audience row. */
  last_power_dialer_connected_at?: string | null;
  vendorid: number;
  website?: string;
  linkedin_url?: string;
  linkedin_enrichment_not_found_at?: string | null;
  linkedin_enrichment_retry_after?: string | null;
  hs_record_id?: string;
  sf_record_id?: string;
  industry?: string;
  dynamic_col_1?: string;
  dynamic_col_2?: string;
  dynamic_col_3?: string;
}

export interface IAudienceCompanyCsvDataUploadedItem {
  address: string;
  city: string;
  company_name: string;
  contact_first_name: string;
  contact_last_name: string;
  created_at: string;
  email: string;
  id: number;
  list_id: number;
  phone: string;
  /** Latest AMD-confirmed human Power Dialer connection for this audience row. */
  last_power_dialer_connected_at?: string | null;
  state: string;
  vendorid: number;
  website: string;
  job_title: string;
  linkedin_url?: string;
  hs_record_id?: string;
  sf_record_id?: string;
  industry?: string;
  dynamic_col_1?: string;
  dynamic_col_2?: string;
  dynamic_col_3?: string;
}

export interface IAudienceImagesDataUploadItem {
  created_at: string;
  id: number;
  image_type: string;
  status: string;
  url: string;
}

// Types for getAvailableWorkflows
interface IAvailableWorkflow {
  audience_id: number;
  audience_name: string;
  play_count: number;
  audience_created_at: string;
}
export interface IGetAvailableWorkflowsResponse {
  workflows: IAvailableWorkflow[];
}

// Types for copyWorkflow
export interface ICopyWorkflowResponse {
  message: string;
  copiedPlays: { audience_id: number }[];
}

export interface ICopyWorkflowBody {
  sourceAudienceId: number;
  targetAudienceId: number;
}

interface IUploadCsvBody {
  type: AUDIENCE_CSV_IMPORT_TYPE;
  template: string;
  data: any;
}

// Customer/Opportunity cross-reference match types
export interface ICustomerMatch {
  row_id: number;
  match_types: ('customer' | 'opportunity')[];
  matched_domains: string[];
  matched_companies: string[];
  rule_names: string[];
}

export interface ICheckCustomerMatchesResponse {
  matches: ICustomerMatch[];
  total_checked: number;
  total_matches: number;
  /**
   * The customer rows alone, where `total_matches` also counts open
   * opportunities (mb-server#6451). The two are reported separately because
   * "Exclude customers" drops only the first set, and saying "12 removed" when
   * 12 included opportunities it kept would be wrong.
   */
  total_customer_matches?: number;
}

// Cross-audience overlap match types (GH#994)
export interface IOverlapAudience {
  audience_id: number;
  audience_name: string;
  creation_method: string;
  people_id: number;
}

export interface IOverlapMatch {
  row_id: number;
  email: string;
  first_name: string;
  last_name: string;
  company_name: string;
  overlap_audiences: IOverlapAudience[];
}

export interface ICheckCrossAudienceResponse {
  matches: IOverlapMatch[];
  total_checked: number;
  total_overlapping: number;
}

/** Metadata-only saved campaign; execution/performance data is intentionally absent. */
export type CampaignTemplate = Pick<IAudienceItem,
  'id' | 'name' | 'campaign_name' | 'description' | 'updated_at' | 'tags' | 'step_count' | 'createdBy'>;
